import { useCallback } from 'react';
import { atom, useAtom } from 'jotai';

type ScaleType = 'quantile' | 'quantize' | 'custom';

type UseLegendConfig = {
  scaleType: ScaleType;
  scaleSize: number;
  customRange: [number, number][];
  draftCustomRange: [number, number][];
  isEditingLegend: boolean;
  setScaleType: (type: ScaleType) => void;
  setScaleSize: (size: number) => void;
  setIsEditingLegend: (isEditing: boolean) => void;
  setCustomRange: (range: [number, number][]) => void;
  setDraftCustomRange: (range: [number, number][]) => void;
  updateDraftCustomRange: (
    index: number,
    bound: 'lower' | 'upper',
    value: number
  ) => void;
  addDraftCustomRange: () => void;
  removeDraftCustomRange: (index: number) => void;
  isValidDraftCustomRange: (index: number, range: [number, number]) => boolean;
  resetLegendConfig: () => void;
};

const MIN_SCALE_SIZE = 1;
const MAX_SCALE_SIZE = 8;

const scaleTypeAtom = atom<ScaleType>('quantile');

const scaleSizeAtom = atom<number>(MAX_SCALE_SIZE);

const customRangeAtom = atom<[number, number][]>([]);

const isEditingLegendAtom = atom<boolean>(false);

const draftCustomRangeAtom = atom<[number, number][]>([]);

const useLegendConfig = (): UseLegendConfig => {
  const [scaleType, setScaleType] = useAtom(scaleTypeAtom);
  const [scaleSize, setScaleSize] = useAtom(scaleSizeAtom);
  const [customRange, setCustomRange] = useAtom(customRangeAtom);
  const [isEditingLegend, setIsEditingLegend] = useAtom(isEditingLegendAtom);
  const [draftCustomRange, setDraftCustomRange] = useAtom(draftCustomRangeAtom);

  const updateDraftCustomRange = useCallback(
    (index: number, bound: 'lower' | 'upper', value: number): void => {
      setDraftCustomRange(
        (currentRange: [number, number][]): [number, number][] => {
          if (index < 0 || currentRange.length <= index) {
            return currentRange;
          }

          const range: [number, number] =
            bound === 'lower'
              ? [value, currentRange[index][1]]
              : [currentRange[index][0], value];
          // 隣接する範囲と隙間ができないように前後も同期して更新するが、不正値については同期しない
          return currentRange.map(
            (current: [number, number], i: number): [number, number] => {
              if (i === index) {
                return range;
              }
              if (i === index - 1) {
                return [
                  current[0],
                  !Number.isNaN(range[0]) ? range[0] : current[1],
                ];
              }
              if (i === index + 1) {
                return [
                  !Number.isNaN(range[1]) ? range[1] : current[0],
                  current[1],
                ];
              }
              return current;
            }
          );
        }
      );
    },
    [setDraftCustomRange]
  );

  const addDraftCustomRange = useCallback((): void => {
    setDraftCustomRange(
      (currentRange: [number, number][]): [number, number][] => {
        if (currentRange.length >= MAX_SCALE_SIZE) {
          return currentRange;
        }
        return [
          ...currentRange,
          [
            currentRange[currentRange.length - 1]?.[1] ?? Number.NaN,
            Number.NaN,
          ],
        ];
      }
    );
  }, [setDraftCustomRange]);

  const removeDraftCustomRange = useCallback(
    (index: number): void => {
      setDraftCustomRange(
        (currentRange: [number, number][]): [number, number][] => {
          if (
            currentRange.length <= MIN_SCALE_SIZE ||
            index < 0 ||
            currentRange.length <= index
          ) {
            return currentRange;
          }
          // 最初の範囲を削除する場合は、次の範囲の最小値を下げて隙間を埋める
          if (index === 0) {
            return [
              [currentRange[0][0], currentRange[1][1]],
              ...currentRange.slice(2),
            ];
          }
          // それ以外の範囲を削除する場合は、前の範囲の最大値を上げて隙間を埋める
          return [
            ...currentRange.slice(0, index - 1),
            [currentRange[index - 1][0], currentRange[index][1]],
            ...currentRange.slice(index + 1),
          ];
        }
      );
    },
    [setDraftCustomRange]
  );

  const isValidDraftCustomRange = useCallback(
    (index: number, range: [number, number]): boolean => {
      if (Number.isNaN(range[0]) || Number.isNaN(range[1])) {
        return false;
      }
      // 範囲の大小関係が合っているか確認する
      if (range[1] < range[0]) {
        return false;
      }
      // 前の範囲よりも大きいことを確認する
      if (0 < index && range[0] < draftCustomRange[index - 1][0]) {
        return false;
      }
      // 次の範囲よりも小さいことを確認する
      if (
        index < draftCustomRange.length - 1 &&
        draftCustomRange[index + 1][1] < range[1]
      ) {
        return false;
      }
      return true;
    },
    [draftCustomRange]
  );

  const resetLegendConfig = useCallback((): void => {
    setScaleType('quantile');
    setScaleSize(MAX_SCALE_SIZE);
    setCustomRange([]);
    setDraftCustomRange([]);
    setIsEditingLegend(false);
  }, [
    setScaleType,
    setScaleSize,
    setCustomRange,
    setDraftCustomRange,
    setIsEditingLegend,
  ]);

  return {
    scaleType,
    scaleSize,
    customRange,
    isEditingLegend,
    draftCustomRange,
    setScaleType,
    setScaleSize,
    setIsEditingLegend,
    setCustomRange,
    setDraftCustomRange,
    updateDraftCustomRange,
    addDraftCustomRange,
    removeDraftCustomRange,
    isValidDraftCustomRange,
    resetLegendConfig,
  };
};

export { useLegendConfig, MIN_SCALE_SIZE, MAX_SCALE_SIZE };
